import { ApolloClient } from "@apollo/client";
import { BILL_LOGO, BILL_SETTINGS, GET_CUSTOMER_BY_ID, LIST_USERS } from "../gql/queries";
import { ReceiptBill } from "./electron";

/**
 * Logos already fetched, by location, kept until that location's logo changes
 * (`logoUpdatedAt`), so a till does not download up to 300 KB for every sale.
 */
const logos = new Map<string, { updatedAt: string; dataUrl: string }>();

const quietly = <T,>(work: Promise<T>, fallback: T): Promise<T> => work.catch(() => fallback);

async function logoFor(client: ApolloClient<any>, shopId: string, locationId: string, updatedAt: string): Promise<string | null> {
	const known = logos.get(locationId);
	if (known && known.updatedAt === updatedAt) return known.dataUrl;
	// The client resolves GraphQL errors instead of throwing (errorPolicy "all"):
	// a failed or empty read must not be remembered as "no logo" for the day.
	const { data, errors } = await client.query({ query: BILL_LOGO, variables: { shopId, locationId } });
	const dataUrl: string | null = data?.billLogo ?? null;
	if (errors?.length || !dataUrl) return null;
	logos.set(locationId, { updatedAt, dataUrl });
	return dataUrl;
}

async function staffNamesFor(client: ApolloClient<any>, shopId: string, locationId: string): Promise<Record<string, string>> {
	const { data } = await client.query({ query: LIST_USERS, variables: { shopId, locationId } });
	return Object.fromEntries(
		(data?.users ?? []).map((u: any) => [u._id, [u.firstName, u.lastName].filter(Boolean).join(" ")])
	);
}

async function customerPhoneFor(client: ApolloClient<any>, customerId: string): Promise<string | null> {
	const { data } = await client.query({ query: GET_CUSTOMER_BY_ID, variables: { id: customerId } });
	return data?.customer?.phone ?? null;
}

/**
 * Everything the till receipt needs beyond the order, for the active location
 * (openspec/changes/location-bill-settings, D6). Undefined when the settings
 * cannot be read, so the receipt prints with defaults rather than block the
 * sale; a missing logo, staff list or phone only leaves that line out.
 */
export async function loadReceiptBill(
	client: ApolloClient<any>,
	shopId: string,
	locationId: string,
	customerId?: string | null
): Promise<ReceiptBill | undefined> {
	try {
		const { data } = await client.query({ query: BILL_SETTINGS, variables: { shopId, locationId } });
		const settings = data?.billSettings;
		if (!settings) return undefined;
		const [logo, staffNames, customerPhone] = await Promise.all([
			settings.hasLogo && settings.logoUpdatedAt
				? quietly(logoFor(client, shopId, locationId, String(settings.logoUpdatedAt)), null)
				: Promise.resolve(null),
			settings.show?.staff ? quietly(staffNamesFor(client, shopId, locationId), {}) : Promise.resolve({}),
			settings.show?.customerPhone && customerId ? quietly(customerPhoneFor(client, customerId), null) : Promise.resolve(null),
		]);
		return { settings, logo, staffNames, customerPhone };
	} catch {
		return undefined;
	}
}
